import map from 'lodash/map'
import noop from 'lodash/noop'
import React, { Fragment, useContext } from 'react'

import { CheckboxItem } from '@Components/Facet/Display/Items/Checkbox'
import { ColorItem } from '@Components/Facet/Display/Items/Color'
import { PriceItem } from '@Components/Facet/Display/Items/Price'
import { RangeItem } from '@Components/Facet/Display/Items/Range'
import { Wordbook } from '@Components/Wordbook'
import { ProductListBlockFacetType } from '@Generated/graphql'
import { useEnumerable, useGroup, useRange } from '@Globals/Algolia/Hooks'
import { FacetConfig, FacetConfigGroup, FacetConfigValue } from '@Globals/Algolia/Types'
import { BlockContext } from '@Globals/CMS/Context'

import styles from './FacetContent.module.scss'

const FacetContentEnumerableValue = ({
  facetType,
  onFilterChange = noop,
  parameter,
  value,
}: {
  facetType?: ProductListBlockFacetType | null
  onFilterChange?: () => void
  parameter: string
  value: FacetConfigValue
}) => {
  const { blockID } = useContext(BlockContext)
  const enumerable = useEnumerable(parameter || '', value.id || '')

  if (!enumerable) {
    return null
  }

  if (facetType === ProductListBlockFacetType.EnumerableColor) {
    return (
      <ColorItem
        checked={enumerable.checked || false}
        color={value.color || ''}
        count={enumerable.count || 0}
        disabled={enumerable.count === 0}
        id={`${blockID}-facet:${parameter}:${value.id}`}
        onChange={() => {
          if (enumerable.onChange) {
            enumerable.onChange()
            onFilterChange()
          }
        }}
        text={<Wordbook wordbook={value.name} />}
      />
    )
  }

  if (facetType === ProductListBlockFacetType.EnumerableDefault
    || facetType === ProductListBlockFacetType.EnumerableBoolean
    || facetType === ProductListBlockFacetType.EnumerableNumeric
  ) {
    return (
      <CheckboxItem
        checked={enumerable.checked || false}
        count={enumerable.count || 0}
        disabled={enumerable.count === 0}
        id={`${blockID}-facet:${parameter}:${value.id}`}
        onChange={() => {
          if (enumerable.onChange) {
            enumerable.onChange()
            onFilterChange()
          }
        }}
        text={<Wordbook wordbook={value.name} />}
      />
    )
  }

  return null
}

const FacetContentRange = ({
  autoApplyFilter,
  facet,
  onFilterChange = noop,
}: {
  autoApplyFilter?: boolean
  facet: FacetConfig
  onFilterChange?: () => void
}) => {
  const { blockID } = useContext(BlockContext)
  const range = useRange(facet.parameter || '', facet.key || '')

  if (!range) {
    return null
  }

  if (facet.type === ProductListBlockFacetType.RangeDefault) {
    return (
      <RangeItem
        from={range.from || 0}
        id={`${blockID}-facet:${facet.parameter}`}
        max={range.stats.max}
        min={range.stats.min}
        onChange={(from, to) => {
          if (range.onChange) {
            range.onChange(from, to)
            onFilterChange()
          }
        }}
        to={range.to || range.stats.max}
      />
    )
  }

  if (facet.type === ProductListBlockFacetType.RangePrice) {
    return (
      <PriceItem
        autoApplyFilter={autoApplyFilter}
        from={range.from || 0}
        id={`${blockID}-facet:${facet.parameter}`}
        max={range.stats.max}
        min={range.stats.min}
        onChange={(from, to) => {
          if (range.onChange) {
            range.onChange(from, to)
            onFilterChange()
          }
        }}
        to={range.to || range.stats.max}
      />
    )
  }

  return null
}

export const FacetContentGroup = ({
  facetType,
  group,
  onFilterChange = noop,
  parameter,
}: {
  facetType?: ProductListBlockFacetType | null
  group: FacetConfigGroup
  onFilterChange?: () => void
  parameter: string
}) => {
  const facetGroup = useGroup(parameter)
  if (!facetGroup) {
    return null
  }

  return (
    <Fragment key={group.key}>
      {facetGroup.showGroupTitle && (
        <span className={styles.groupName}>
          <Wordbook wordbook={group.name} />
        </span>
      )}
      {map(group.values, value => (
        <FacetContentEnumerableValue
          key={value.id}
          facetType={facetType}
          onFilterChange={onFilterChange}
          parameter={parameter || ''}
          value={value}
        />
      ))}
    </Fragment>
  )
}

export const FacetContent = ({
  autoApplyFilter,
  facet,
  onFilterChange = noop,
}: {
  autoApplyFilter?: boolean
  facet: FacetConfig
  onFilterChange?: () => void
}) => (
  <div className={styles.facetContent}>
    {(facet.type === ProductListBlockFacetType.EnumerableDefault
      || facet.type === ProductListBlockFacetType.EnumerableBoolean
      || facet.type === ProductListBlockFacetType.EnumerableColor
      || facet.type === ProductListBlockFacetType.EnumerableNumeric
    ) && (
      <>
        {map(facet.values, value => (
          <FacetContentEnumerableValue
            key={value.key}
            facetType={facet.type}
            onFilterChange={onFilterChange}
            parameter={facet.parameter || ''}
            value={value}
          />
        ))}
        {map(facet.groups, group => (
          <FacetContentGroup
            key={group.key}
            facetType={facet.type}
            group={group}
            onFilterChange={onFilterChange}
            parameter={facet.parameter || ''}
          />
        ))}
      </>
    )}

    {(facet.type === ProductListBlockFacetType.RangeDefault || facet.type === ProductListBlockFacetType.RangePrice) && (
      <FacetContentRange
        key={facet.id}
        autoApplyFilter={autoApplyFilter}
        facet={facet}
        onFilterChange={onFilterChange}
      />
    )}
  </div>
)
